import { FC, useRef, useState } from 'react'
import { Marker, InfoBox } from '@react-google-maps/api';
import { Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { format as formatDate } from 'date-fns';

import { usePreferences } from '../../utility/usePreferences';
import { IWaypointMarker, WaypointMarkerType } from '../../logic/api';

const getStrengthLabel = (strength: number | undefined): string => {
  switch (strength) {
    case 1:
      return 'map.labels.strengthLow';
    case 2:
      return 'map.labels.strengthMedium';
    case 3:
      return 'map.labels.strengthHigh';
    default:
      return 'map.labels.strengthUnknown';
  }
}
interface IMakerTooltipProps {
  marker: IWaypointMarker;
}

export const MarkerTooltip: FC<IMakerTooltipProps> = ({ marker }) => {
  const { t } = useTranslation()
  const { formatDistance } = usePreferences()

  let title = '';
  let body = '';
  switch (marker.type) {
    case WaypointMarkerType.START:
      title = t('map.labels.journeyStarted')
      break

    case WaypointMarkerType.END:
      title = t('map.labels.journeyFinished')
      break

    case WaypointMarkerType.SPEEDING:
      title = t('map.labels.speeding')
      body = `${formatDistance(marker.speed)}/${formatDistance(marker.speedLimit)}`
      break

    case WaypointMarkerType.ACCELERATION:
      title = t('map.labels.accelerating')
      body = t(getStrengthLabel(marker.strength), { strength: marker.strength })
      break

    case WaypointMarkerType.BRAKING:
      title = t('map.labels.braking')
      body = t(getStrengthLabel(marker.strength), { strength: marker.strength })
      break

    case WaypointMarkerType.CORNERING:
      title = t('map.labels.cornering')
      body = t(getStrengthLabel(marker.strength), { strength: marker.strength })
      break
  }

  return (
    <Box sx={{
      bgcolor: 'background.paper',
      p: 1,
      borderRadius: 2,
      border: '1px solid #ccc',
      minWidth: 80,
      whiteSpace: "nowrap"
    }}>
      <Typography variant="caption">
        {formatDate(new Date(marker.timestamp), 'do LLL, HH:mm')}
      </Typography>
      <Typography variant="subtitle2">
        {title}
      </Typography>
      <Typography variant="body2">
        {body}
      </Typography>
    </Box>
  )
}

interface IGoogleMapsMarkerProps {
  position: google.maps.LatLngLiteral
  icon: google.maps.Icon
  tooltip?: JSX.Element
}

export const GoogleMapsMarker: FC<IGoogleMapsMarkerProps> = ({ tooltip, icon, position }) => {
  const [isHovered, setIsHovered] = useState(false)
  const ref = useRef<Marker>(null)

  const handleMouseEnter = () => {
    setIsHovered(true)
  }

  const handleMouseLeave = () => {
    setIsHovered(false)
  }

  return (
    <>
      <Marker
        ref={ref}
        icon={icon}
        position={position}
        onMouseOver={handleMouseEnter}
        onMouseOut={handleMouseLeave}
      />
      {(isHovered && tooltip) && (
        <InfoBox
          anchor={ref.current?.marker}
          options={{
            closeBoxURL: '',
            pixelOffset: new google.maps.Size(24, -32),
          }}
        >
          {tooltip}
        </InfoBox>
      )}
    </>
  )
}
